import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ChevronDown } from 'lucide-react'
import { useTeamEventUpdateContext } from '../context/TeamEventUpdateContext'
import { eventActivitiesNoun, eventActivityNoun } from './eventActivityNoun'
import capitalize from 'lodash/capitalize'

type Props = {
  event: wowaudit.Event
  activities: wowaudit.EventActivity[]
  canEdit: boolean
}

const eventAvatarUrl = (event: wowaudit.Event) =>
  event.instances[0]?.thumbnailUrl ?? event.instances[0]?.tileUrl

const activityAvatarUrl = (activity: wowaudit.EventActivity) =>
  activity.encounter?.thumbnailUrl ??
  activity.instance?.thumbnailUrl ??
  activity.instance?.tileUrl

const activityLabel = (activity: wowaudit.EventActivity) =>
  activity.encounter?.name ||
  activity.instance?.name ||
  capitalize(eventActivityNoun(activity).singular)

const Avatar = ({ src }: { src?: string | null }) =>
  src ? (
    <img
      className='w-8 h-8 rounded-md border-2 border-border shrink-0'
      src={src}
    />
  ) : (
    <div className='w-8 h-8 rounded-md border-2 border-border bg-muted shrink-0' />
  )

const EventPrimaryActivitySelect = ({ event, activities, canEdit }: Props) => {
  const { updateEvent, updateEventActivity } = useTeamEventUpdateContext()
  const primaryActivity = activities.find(
    (activity) => activity.id === event.primaryActivityId,
  )
  const allActivitiesLabel = `All ${eventActivitiesNoun(activities).plural}`

  const selectPrimaryActivity = async (activity: wowaudit.EventActivity) => {
    await updateEvent({ primaryActivityId: activity.id })
    if (!activity.active) {
      await updateEventActivity(activity.id, { active: true })
    }
  }

  const currentAvatar = primaryActivity
    ? activityAvatarUrl(primaryActivity)
    : eventAvatarUrl(event)
  const currentLabel = primaryActivity
    ? activityLabel(primaryActivity)
    : allActivitiesLabel

  const title = (
    <span className='flex items-center gap-3 min-w-0'>
      <Avatar src={currentAvatar} />
      <span className='flex flex-col min-w-0 text-left'>
        <h3 className='m-0 truncate'>{currentLabel}</h3>
        {canEdit && primaryActivity && (
          <span className='text-xs text-muted-foreground italic font-normal truncate'>
            Members will see only this{' '}
            {eventActivityNoun(primaryActivity).singular}
          </span>
        )}
      </span>
    </span>
  )

  if (!canEdit) {
    return <div className='flex items-center'>{title}</div>
  }

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button variant='ghost' className='h-auto max-w-full gap-2 px-2 py-1'>
          {title}
          <ChevronDown className='h-4 w-4 shrink-0 text-muted-foreground' />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align='start'
        className='w-fit max-h-102 overflow-y-auto'
      >
        <DropdownMenuItem
          onSelect={() => void updateEvent({ primaryActivityId: null })}
        >
          <span className='flex items-center gap-2 min-w-0'>
            <Avatar src={eventAvatarUrl(event)} />
            <span
              className={cn(
                'truncate',
                event.primaryActivityId == null && 'text-primary',
              )}
            >
              {allActivitiesLabel}
            </span>
          </span>
        </DropdownMenuItem>

        {activities.map((activity) => (
          <DropdownMenuItem
            key={activity.id}
            onSelect={() => void selectPrimaryActivity(activity)}
          >
            <span
              className={cn(
                'flex items-center gap-2 min-w-0',
                !activity.active && 'opacity-60',
              )}
            >
              <Avatar src={activityAvatarUrl(activity)} />
              <span
                className={cn(
                  'truncate',
                  event.primaryActivityId === activity.id && 'text-primary',
                )}
              >
                {activityLabel(activity)}
              </span>
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export default EventPrimaryActivitySelect
